import type { FC } from 'react';
import React, { memo, useCallback, useContext, useState } from 'react';
import { css } from '@compiled/react';
import { graphql } from 'react-relay';
import type { MemoizedFn } from 'memoize-one';
import memoizeOne from 'memoize-one';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import traceUFOHover from '@atlaskit/react-ufo/trace-hover';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { isEmbeddedRovoSystemSpaceAccessAllowed } from '@confluence/route-manager/entry-points/isEmbeddedRovoSystemSpaceAccessAllowed.ts';
import { isRovoSystemSpaceKey } from '@confluence/route-manager/entry-points/isRovoSystemSpaceKey.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePageSpaceKey } from '@confluence/page-context';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	SPACE_OVERVIEW_SHORTCUT,
	UNIVERSAL_SPACE_OVERVIEW_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { RestrictionsDialogQuery } from '@confluence/restrictions/entry-points/RestrictionsDialogQuery.graphql.ts';
import {
	isSpaceNotFoundError,
	isSpaceRestrictedError,
} from '@confluence/restrictions/entry-points/Query.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import {
	PERFORMANCE_SUBJECT_navigation,
	PERFORMANCE_SUBJECT_sideNavigationFMP,
} from '@confluence/performance/entry-points/constants.ts';
import { PerformanceEnd } from '@confluence/performance/entry-points/PerformanceEnd.ts';
import { PerformanceStart } from '@confluence/performance/entry-points/PerformanceStart.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { FORGE_MODULE_SPACE_PAGE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { useExtensionList } from '@confluence/forge-ui/entry-points/useExtensionList.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { FocusToCurrentPageTreeLinkItemSSRInlineScript } from '@confluence/page-tree/entry-points/FocusToCurrentPageTreeLinkItemSSRInlineScript.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';
import { useSimplifiedSpaceNav } from '@confluence/experiment-simplify-nav-new-user/entry-points/useSimplifiedSpaceNav.ts';
import { applySpaceNavD0Overrides } from '@confluence/experiment-simplify-nav-new-user/entry-points/applySpaceNavD0Overrides.ts';

import type {
	SpaceNavigationQuery$data,
	SpaceNavigationQuery$variables,
} from './__generated__/SpaceNavigationQuery.graphql.ts';
import { SPACE_NAVIGATION_METRIC } from './perf.config.ts';
import { getAllAppLinks } from './space-apps-helpers.ts';

type SpaceNavigationQuery_space = NonNullable<SpaceNavigationQuery$data['space']>;
type SpaceNavigationQuery_spaceSidebarLinks_main = ApolloInteropMutableHelper<
	NonNullable<
		NonNullable<NonNullable<SpaceNavigationQuery$data['spaceSidebarLinks']>['main']>[number]
	>
>;
type SpaceNavigationQuery_spaceSidebarLinks_quick = ApolloInteropMutableHelper<
	NonNullable<
		NonNullable<NonNullable<SpaceNavigationQuery$data['spaceSidebarLinks']>['quick']>[number]
	>
>;

const SpaceNavigationQuery = graphql`
	query SpaceNavigationQuery(
		$cloudId: ID!
		$spaceKey: String!
		$isLicensed: Boolean!
		$includePersistentInviteLicense: Boolean = false
		$includeSpaceCurationCount: Boolean = false
		$contentId: ID
		$ptStatuses: [PTGraphQLPageStatus]
		$paginationLimit: Int
		$isSuperAdmin: Boolean = false
		$includePageTree: Boolean = false
		$includeHomepageTreeVariant: Boolean = false
		$includeBlogTree: Boolean = false
		$blogFirst: Int = 40
		$blogOrderBy: String = "metadata.createdDate desc"
		$blogStatuses: [String] = ["current", "draft"]
		$includeSpaceViewsPersistence: Boolean = false
	) {
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				permissionType
			}
		}
		...useFetchSuggestionCount_query
			@alias(as: "spaceCurationSuggestionCount")
			@include(if: $includeSpaceCurationCount)
		license @include(if: $includePersistentInviteLicense) {
			...PersistentInviteEligibleQuery_license
		}
		spaceSidebarLinks(spaceKey: $spaceKey) {
			...SpaceLinks_spaceSidebarLinks
			...ShortcutsSection_spaceSidebarLinks
			main(includeHidden: true) {
				id
				webItemKey
				webItemCompleteKey
				title
				url
				position
				styleClass
				icon {
					path
					width
					height
					isDefault
				}
				iconClass
				hidden
				canHide
				tooltip
				linkIdentifier
				type
			}
			quick {
				id
				url
				position
				title
				styleClass
			}
		}
		space(key: $spaceKey) {
			...useIsUserViewingSpaceAsAnon_space
			...PersistentInviteEligibleQuery_space
			...ExternalCollaboratorsSpaceInformationMessage_space
			...SpaceHeader_space @arguments(isLicensed: $isLicensed)
			...CommonSpaceNavigationContent_space @relay(mask: false)
			...useSpaceViewsData_space
			id
			key
			name
			type
			status
			icon {
				path
			}
			homepage {
				id
			}
			homepageId
			currentUser @include(if: $isLicensed) {
				isAdmin
				isFavourited
				isWatched
			}
			operations {
				operation
				targetType
			}
			containsExternalCollaborators
			history {
				createdDate
			}
			didContainUserContent
			pageTree(pageTree: $paginationLimit, status: $ptStatuses, enablePaging: true)
				@include(if: $includeHomepageTreeVariant) {
				...PageTreeQuery_ptpage
				nearestAncestors {
					pageInfo {
						hasNextPage
						endCursor
					}
					nodes {
						...PageTreeQuery_ptpage
						previousSiblings {
							nodes {
								...PageTreeQuery_ptpage
							}
							pageInfo {
								hasNextPage
							}
						}
						followingSiblings {
							nodes {
								...PageTreeQuery_ptpage
							}
							pageInfo {
								hasNextPage
							}
						}
					}
				}
				previousSiblings {
					nodes {
						...PageTreeQuery_ptpage
					}
					pageInfo {
						hasNextPage
					}
				}
				followingSiblings {
					nodes {
						...PageTreeQuery_ptpage
					}
					pageInfo {
						hasNextPage
					}
				}
				children {
					nodes {
						...PageTreeQuery_ptpage
					}
					pageInfo {
						hasNextPage
					}
				}
			}
		}
		currentPage: ptpage(
			id: $contentId
			spaceKey: $spaceKey
			pageTree: $paginationLimit
			status: $ptStatuses
			enablePaging: true
		) @include(if: $includePageTree) {
			...PageTreeQuery_ptpage
			nearestAncestors {
				pageInfo {
					hasNextPage
					endCursor
				}
				nodes {
					...PageTreeQuery_ptpage
					previousSiblings {
						nodes {
							...PageTreeQuery_ptpage
						}
						pageInfo {
							hasNextPage
						}
					}
					followingSiblings {
						nodes {
							...PageTreeQuery_ptpage
						}
						pageInfo {
							hasNextPage
						}
					}
				}
			}
			previousSiblings {
				nodes {
					...PageTreeQuery_ptpage
				}
				pageInfo {
					hasNextPage
				}
			}
			followingSiblings {
				nodes {
					...PageTreeQuery_ptpage
				}
				pageInfo {
					hasNextPage
				}
			}
			children {
				nodes {
					...PageTreeQuery_ptpage
				}
				pageInfo {
					hasNextPage
				}
			}
		}
		rootPageTree: ptpage(
			spaceKey: $spaceKey
			pageTree: $paginationLimit
			status: $ptStatuses
			enablePaging: true
		) @include(if: $includePageTree) {
			...PageTreeQuery_ptpage
			followingSiblings {
				nodes {
					...PageTreeQuery_ptpage
				}
				pageInfo {
					hasNextPage
				}
			}
		}
		homepageRootPageTree: ptpage(spaceKey: $spaceKey, status: $ptStatuses)
			@include(if: $includeHomepageTreeVariant) {
			...PageTreeQuery_ptpage
			followingSiblings {
				nodes {
					...PageTreeQuery_ptpage
				}
				pageInfo {
					hasNextPage
				}
			}
		}
		blogPosts: content(
			spaceKey: $spaceKey
			type: "blogpost"
			first: $blogFirst
			orderby: $blogOrderBy
			status: $blogStatuses
		) @include(if: $includeBlogTree) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				...BlogTreeRootComponent_content
			}
		}
		userPreferences @include(if: $includeSpaceViewsPersistence) {
			...useSpaceViewsOptionsPersistence_userPreferences
		}
	}
`;
import { SHORTCUTS_KEY, BLOG_KEY } from './webItemCompleteKeys.ts';
import { SpaceNavigationContent } from './SpaceNavigationContentLoaders.ts';

const SPACE_NAV_ID = 'app-navigation-space-container';

const MANAGE_USERS = 'manage_users';
const MANAGE_NONLICENSED_USERS = 'manage_nonlicensed_users';
const MANAGE_GUEST_USERS = 'manage_guest_users';
const ARCHIVE = 'archive';
const ADMINISTER = 'administer';
const MANAGE_CONTENT = 'manage_content';
const MANAGE_PUBLIC_LINKS = 'manage_public_links';
const VIEW_ANALYTICS = 'view_analytics';

const spaceNavigationContainerStyles = css({
	// Some child elements are absolutely or sticky positioned based on this container - such as borders within the SpaceHeader.
	position: 'relative',
});

// Establishes a stacking context above the gsn-global-shortcuts section that
// follows later in the DOM inside the sidebar's isolated stacking context.
// Inline contextual-create popovers (rendered in-place for a11y) live inside
// this container — without an explicit z-index they lose to the shortcuts block
// which comes later in source order. z-index: 2 beats the shortcuts' default
// z-index:auto within the shared sidebar stacking context.
const spaceNavigationContainerStackingFixStyles = css({
	zIndex: 2,
});

const SpaceNavigationContainer = ({
	'data-testid': testId,
	'data-vc': dataVc,
	'data-ssr-placeholder-replace': dataSSRPlaceholderReplace,
	onMouseEnter,
	children,
}: {
	'data-testid': string;
	'data-vc': string;
	onMouseEnter: () => void;
	'data-ssr-placeholder-replace'?: string;
	children: React.ReactNode;
}) => (
	<div
		css={[spaceNavigationContainerStyles, spaceNavigationContainerStackingFixStyles]}
		data-testid={testId}
		data-vc={dataVc}
		data-ssr-placeholder-replace={dataSSRPlaceholderReplace}
		onMouseEnter={onMouseEnter}
		onFocus={onMouseEnter}
	>
		{children}
	</div>
);

export type SpaceNavigationProps = {
	isBlogNavigation?: boolean;
	isSpaceSettingsScreen?: boolean;
};

type PreloadSpaceNavigationResult = {
	loading: boolean;
	hasRestrictedError: boolean;
	hasNotFoundError: boolean;
	unhandledError?: ApolloErrorInterop;
	hasSpace: boolean;
	space?: SpaceNavigationQuery_space;
	name?: string;
	iconPath?: string;
	isSpaceAdmin: boolean;
	canManageSomeUsers: boolean;
	canAdminister: boolean;
	canManageContent: boolean;
	canManagePublicLinks: boolean;
	canViewAnalytics: boolean;
	homepageId?: string;
	containsExternalCollaborators: boolean;
	main: SpaceNavigationQuery_spaceSidebarLinks_main[];
	quick: SpaceNavigationQuery_spaceSidebarLinks_quick[];
	isUserExternalCollaborator: boolean;
	isSpaceArchived: boolean;
	hasDeleteSpacePermission: boolean;
	hasArchiveSpacePermission: boolean;
};

const REMOTE_OBJECT_SUGGESTION_COUNT_FIELD = 'confluence_remoteObjectSuggestionCount';

export function isOnlyRemoteObjectSuggestionCountError(error: ApolloErrorInterop): boolean {
	const graphQLErrors = error.graphQLErrors;
	if (!graphQLErrors || graphQLErrors.length === 0) {
		return false;
	}
	return graphQLErrors.every(
		({ path }) => Array.isArray(path) && path.includes(REMOTE_OBJECT_SUGGESTION_COUNT_FIELD),
	);
}

export function isExpectedSuggestionCountError(error: ApolloErrorInterop): boolean {
	const graphQLErrors = error.graphQLErrors;
	if (!graphQLErrors || graphQLErrors.length === 0) {
		return false;
	}
	return graphQLErrors.every(({ extensions }) => {
		const statusCode = (extensions as { statusCode?: number } | undefined)?.statusCode;
		return typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500;
	});
}

export const createResult: MemoizedFn<
	(
		loading: boolean,
		data: SpaceNavigationQuery$data | undefined,
		error: ApolloErrorInterop | undefined,
	) => PreloadSpaceNavigationResult
> = memoizeOne(
	(
		loading: boolean,
		data: SpaceNavigationQuery$data | undefined,
		error: ApolloErrorInterop | undefined,
	): PreloadSpaceNavigationResult => {
		const result: PreloadSpaceNavigationResult = {
			loading,
			hasSpace: false,
			isSpaceAdmin: false,
			canManageSomeUsers: false,
			canAdminister: false,
			canManageContent: false,
			canManagePublicLinks: false,
			canViewAnalytics: false,
			containsExternalCollaborators: false,
			main: [],
			quick: [],
			isUserExternalCollaborator: false,
			hasRestrictedError: false,
			hasNotFoundError: false,
			isSpaceArchived: false,
			hasDeleteSpacePermission: false,
			hasArchiveSpacePermission: false,
		};

		if (loading) {
			return result;
		}

		if (error && !isOnlyRemoteObjectSuggestionCountError(error)) {
			if (isSpaceRestrictedError(error)) {
				result.hasRestrictedError = true;
			} else if (isSpaceNotFoundError(error)) {
				result.hasNotFoundError = true;
			} else {
				result.unhandledError = error;
			}
			return result;
		}

		result.hasSpace = Boolean(data?.space ?? result.hasSpace);
		result.space = data?.space || undefined;
		result.name = data?.space?.name || undefined;
		result.iconPath = `${CONTEXT_PATH}${data?.space?.icon?.path}`;
		result.isSpaceAdmin = data?.space?.currentUser?.isAdmin ?? result.isSpaceAdmin;
		result.canManageSomeUsers =
			data?.space?.operations?.some(
				(operation) =>
					operation?.operation === MANAGE_USERS ||
					operation?.operation === MANAGE_NONLICENSED_USERS ||
					operation?.operation === MANAGE_GUEST_USERS,
			) ?? result.canManageSomeUsers;
		result.canAdminister =
			data?.space?.operations?.some(
				(operation) => operation?.operation === ADMINISTER && operation?.targetType === 'space',
			) ?? result.canAdminister;
		result.canManageContent =
			data?.space?.operations?.some(
				(operation) => operation?.operation === MANAGE_CONTENT && operation?.targetType === 'space',
			) ?? result.canManageContent;
		result.canManagePublicLinks =
			data?.space?.operations?.some(
				(operation) =>
					operation?.operation === MANAGE_PUBLIC_LINKS && operation?.targetType === 'space',
			) ?? result.canManagePublicLinks;
		result.canViewAnalytics =
			data?.space?.operations?.some(
				(operation) => operation?.operation === VIEW_ANALYTICS && operation?.targetType === 'space',
			) ?? result.canViewAnalytics;
		result.homepageId = data?.space?.homepage?.id || undefined;
		result.containsExternalCollaborators =
			data?.space?.containsExternalCollaborators ?? result.containsExternalCollaborators;

		result.main = data?.spaceSidebarLinks?.main?.filter(isNonNullable) ?? result.main;
		result.quick = data?.spaceSidebarLinks?.quick?.filter(isNonNullable) ?? result.quick;
		result.isUserExternalCollaborator = data?.user?.confluence?.permissionType === 'EXTERNAL';
		result.isSpaceArchived = data?.space?.status === 'archived';
		if (data?.space?.operations) {
			result.hasDeleteSpacePermission = data.space.operations?.some(
				(operation) => operation?.operation === 'delete' && operation?.targetType === 'space',
			);
			result.hasArchiveSpacePermission = data.space.operations?.some(
				(operation) => operation?.operation === ARCHIVE && operation?.targetType === 'space',
			);
		}
		return result;
	},
);

const usePreloadSpaceNavigation = (
	spaceKey: string,
	isEditorPage: boolean,
	location: React.ContextType<typeof RoutesContext>['location'],
) => {
	const { cloudId, edition, isLicensed, isLoggedIn } = useSessionData();
	const includePersistentInviteLicense =
		edition !== ConfluenceEdition.FREE && isLoggedIn && isLicensed;
	const includeSpaceCurationCount =
		UNSAFE_expValEqualsNoExposure('confluence_autonomous_space_curation', 'isEnabled', true) &&
		expValEquals('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', true);

	const {
		loading,
		data,
		error,
		variables: routeReaderVariables,
	} = useQueryInterop<SpaceNavigationQuery$data, SpaceNavigationQuery$variables>(
		SpaceNavigationQuery,
		{
			errorPolicy: 'all',
			variables: {
				cloudId,
				spaceKey,
				isLicensed,
				includePersistentInviteLicense,
				includeSpaceCurationCount,
				includeSpaceViewsPersistence: isLicensed,
			},
		},
	);

	const result = createResult(loading, data, error);
	const isEmbeddedRovoSystemSpaceAllowed = isEmbeddedRovoSystemSpaceAccessAllowed({
		isEditorPage,
		location,
	});

	if (isRovoSystemSpaceKey(spaceKey) && !isEmbeddedRovoSystemSpaceAllowed) {
		result.hasNotFoundError = true;
	}

	if (loading) return { ...result, routeReaderVariables };

	if (error) {
		if (isSpaceRestrictedError(error)) {
			markErrorAsHandled(error);
		} else if (isSpaceNotFoundError(error)) {
			markErrorAsHandled(error);
		} else if (
			isOnlyRemoteObjectSuggestionCountError(error) &&
			isExpectedSuggestionCountError(error)
		) {
			markErrorAsHandled(error);
		}
	}

	return { ...result, routeReaderVariables };
};

const useOldRestrictionsButtonRefetch = () => {
	const refetch = useCallback((contentId: any) => {
		/**
		 * When the PageTree child component informs us that a page has been
		 * moved in the hierarchy, we execute a fetch of its new permissions
		 * to purge any stale permissions information that the cache may have
		 * held for that page. Not doing so would mean showing stale state for
		 * its padlock icon, and for its restriction dialog, when raised.
		 */
		void queryInterop({
			query: RestrictionsDialogQuery,
			variables: {
				contentId,
			},
			fetchPolicy: 'network-only',
		});
	}, []);

	return refetch;
};

export const SpaceNavigation: FC<SpaceNavigationProps> = memo(
	({ isBlogNavigation, isSpaceSettingsScreen: _isSpaceSettingsScreen = false }) => {
		const [pageTreeFinishedLoading, setPageTreeFinishedLoading] = useState<boolean>(false);
		const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const [stateSpaceKey] = usePageSpaceKey();

		const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
		const isOnEditRoute = useIsEditorPage();

		// @ts-ignore FIXME: `stateSpaceKey` can be `undefined` here, and needs proper handling
		const spaceKey: string = stateSpaceKey;

		const { push, match, location } = useContext(RoutesContext);

		// On a successful move, tell the restrictions button icon that it may need
		// to be updated
		const onDragDropSuccess = useOldRestrictionsButtonRefetch();

		const onOverviewShortcutTrigger = useCallback(() => {
			push(`/wiki/spaces/${spaceKey}/overview`);
		}, [push, spaceKey]);

		const handleMouseEnter = () => {
			traceUFOHover('space-navigation-hover');

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'entered',
					actionSubject: 'spaceNavigation',
					source: match?.name,
					attributes: {
						navdexPointType: START_TOUCH,
					},
				},
			}).fire();
		};

		const {
			loading,
			hasRestrictedError,
			hasNotFoundError,
			unhandledError,
			hasSpace,
			name,
			space,
			iconPath,
			isSpaceAdmin,
			canManageSomeUsers,
			canAdminister,
			canManageContent,
			canManagePublicLinks,
			canViewAnalytics,
			homepageId,
			containsExternalCollaborators,
			main,
			quick,
			isUserExternalCollaborator,
			isSpaceArchived,
			hasDeleteSpacePermission,
			hasArchiveSpacePermission,
			routeReaderVariables,
		} = usePreloadSpaceNavigation(spaceKey, isOnEditRoute, location);

		const {
			loading: loadingForgeApps,
			extensions: forgeApps,
			error: forgeError,
		} = useExtensionList({
			moduleType: FORGE_MODULE_SPACE_PAGE,
			spaceContextOnly: true,
		});

		if (forgeError && !isErrorMarkedAsHandled(forgeError)) {
			getMonitoringClient().submitError(forgeError, {
				attribution: Attribution.ECOSYSTEM,
			});
			markErrorAsHandled(forgeError);
		}

		const { allAppLinks, allVisibleAppLinks, shouldRenderSpaceApps } = getAllAppLinks(
			spaceKey,
			main,
			forgeApps,
			isSpaceAdmin,
		);

		const shortcutsHidden =
			main.filter(
				({ webItemCompleteKey, hidden }) => webItemCompleteKey === SHORTCUTS_KEY && hidden,
			).length > 0;

		// check if blogs should show. Blogs are default off if the webitem does not exist.
		const blogsWebItem = main.find(({ webItemCompleteKey }) => webItemCompleteKey === BLOG_KEY);
		const blogsHidden = !blogsWebItem || blogsWebItem.hidden === true;

		const showShortcutsSection = !loading && !shortcutsHidden;

		// On a new user's first day (D0), hide empty nav items (shortcuts, blogs,
		// calendar) to reduce clutter.
		// When eligible, merge overrides with space settings; otherwise no change.
		const d0Overrides = useSimplifiedSpaceNav({ spaceKey, quick });
		const visibility = d0Overrides
			? applySpaceNavD0Overrides({ showShortcutsSection, blogsHidden, main }, d0Overrides)
			: { showShortcutsSection, blogsHidden, main };

		const onContentTreeLoadComplete = useCallback(() => {
			setPageTreeFinishedLoading(true);
		}, []);

		const spaceId = space?.id || '';
		const spaceName = name || '';
		const isStarred = Boolean(space?.currentUser?.isFavourited);
		const isWatched = Boolean(space?.currentUser?.isWatched);

		const navigationProps = {
			loading,
			hasSpace,
			space,
			spaceKey,
			homepageId,
			main: visibility.main,
			quick,
			isSpaceAdmin,
			canManageSomeUsers,
			isBlogNavigation,
			blogsHidden: visibility.blogsHidden,
			pageTreeFinishedLoading,
			onDragDropSuccess,
			showShortcutsSection: visibility.showShortcutsSection,
			shouldRenderSpaceApps,
			allAppLinks,
			allVisibleAppLinks,
			loadingForgeApps,
			onContentTreeLoadComplete,
			routeReaderVariables,
		};

		const errorProps = {
			unhandledError,
			hasRestrictedError,
			hasNotFoundError,
		};

		const spaceDetails = {
			spaceId,
			spaceName,
			spaceKey,
			homepageId,
			iconPath,
			isStarred,
			isWatched,
			isSpaceAdmin,
			canManageSomeUsers,
			canAdminister,
			canManageContent,
			canManagePublicLinks,
			canViewAnalytics,
			isPersonalSpace: space?.type === 'personal',
			containsExternalCollaborators,
			isUserExternalCollaborator,
			isSpaceArchived,
			hasDeleteSpacePermission,
			hasArchiveSpacePermission,
		};

		return (
			<>
				<SpaceNavigationContainer
					data-testid={SPACE_NAV_ID}
					data-vc="space-navigation"
					onMouseEnter={handleMouseEnter}
					{...ssrPlaceholderIdProp}
				>
					<PerformanceStart subject={PERFORMANCE_SUBJECT_navigation} subjectId="SpaceViewLoading" />
					{!loading && hasSpace && (
						<>
							<SpaceNavigationContent
								navigationProps={navigationProps}
								errorProps={errorProps}
								spaceDetails={spaceDetails}
							/>
						</>
					)}
					<GeneralShortcutListener
						key="overview-shortcut"
						deprecatedAccelerator={
							!(isLiveEditMode || isOnEditRoute) ? SPACE_OVERVIEW_SHORTCUT : undefined
						}
						accelerator={UNIVERSAL_SPACE_OVERVIEW_SHORTCUT}
						listener={onOverviewShortcutTrigger}
					/>
					<PerformanceEnd
						subject={PERFORMANCE_SUBJECT_sideNavigationFMP}
						subjectId="SideNavigationFMP"
						includeFeatureFlags
					/>
					{!loading && <PageSegmentLoadEnd key={spaceKey} metric={SPACE_NAVIGATION_METRIC} />}
					{unhandledError && <ErrorDisplay error={unhandledError} />}
					{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
					{(process.env.REACT_SSR || window.__SSR_RENDERED__) && (
						<FocusToCurrentPageTreeLinkItemSSRInlineScript />
					)}
				</SpaceNavigationContainer>
			</>
		);
	},
);

function isNonNullable<T extends object | null | undefined>(x: T): x is NonNullable<T> {
	return Boolean(x);
}
